import { Geolocation } from '@capacitor/geolocation';
import { isNative } from './platform';

export interface Fix {
  lat: number;
  lng: number;
  /** 定位精度半径（米），越小越准 */
  accuracyM: number | null;
  /** 取得这次定位的时刻 */
  at: Date;
  /** Android 检测到的模拟定位标记；Web 端拿不到，恒为 false */
  isMocked: boolean;
}

export class LocationError extends Error {
  constructor(readonly kind: 'DENIED' | 'UNAVAILABLE' | 'TIMEOUT') {
    super(kind);
    this.name = 'LocationError';
  }
}

/** 单次定位的超时。工地上首次定位可能要十几秒，给足时间但不能无限等。 */
const TIMEOUT_MS = 20_000;

/**
 * 取一次高精度定位。
 *
 * 打卡和隐蔽工程拍照都用它。注意：**这里拿到的坐标只是上报用**，
 * 是否在围栏内由服务端判定 —— 客户端算的话改前端代码就能伪造位置。
 */
export async function getCurrentFix(): Promise<Fix> {
  if (!isNative) return getWebFix();

  try {
    const permission = await Geolocation.checkPermissions();
    if (permission.location !== 'granted') {
      const requested = await Geolocation.requestPermissions({ permissions: ['location'] });
      if (requested.location !== 'granted') throw new LocationError('DENIED');
    }

    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: TIMEOUT_MS,
      // 不接受缓存位置：工人可能刚从别处过来，用旧坐标打卡等于放任作弊
      maximumAge: 0,
    });

    return {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracyM: position.coords.accuracy ?? null,
      at: new Date(position.timestamp),
      // Capacitor 的类型里没有这个字段，但安卓原生 Location 有，运行时能读到
      isMocked: Boolean((position.coords as unknown as { isMocked?: boolean }).isMocked),
    };
  } catch (error) {
    if (error instanceof LocationError) throw error;
    throw toLocationError(error);
  }
}

/** 浏览器降级：管理端补录、开发调试都走这条 */
function getWebFix(): Promise<Fix> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new LocationError('UNAVAILABLE'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracyM: position.coords.accuracy ?? null,
          at: new Date(position.timestamp),
          isMocked: false,
        }),
      (error) => {
        if (error.code === error.PERMISSION_DENIED) reject(new LocationError('DENIED'));
        else if (error.code === error.TIMEOUT) reject(new LocationError('TIMEOUT'));
        else reject(new LocationError('UNAVAILABLE'));
      },
      { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: 0 },
    );
  });
}

function toLocationError(error: unknown): LocationError {
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  if (message.includes('denied') || message.includes('permission')) {
    return new LocationError('DENIED');
  }
  if (message.includes('timeout')) return new LocationError('TIMEOUT');
  return new LocationError('UNAVAILABLE');
}
